// ============================================================================
// PROPOSAL TAB FOR UNIFIED OVERVIEW
// Renders a finance-style proposal workspace with a compact AI sidecar and a
// proposal switcher for work-item-linked proposals.
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText,
  Sparkles,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  DollarSign,
  Eye,
  ExternalLink,
  Loader2,
  AlertCircle,
  Bot,
  ShieldCheck,
  RefreshCw,
  FileStack,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { getApiUrl } from '@/lib/apiConfig';
import { fetchWithRetry } from '@/lib/fetchUtils';
import { useAiProposalManagerSettings } from '@/hooks/useAiProposalManagerSettings';
import { notify } from '@/lib/notifications';
import { extractProposalReferenceDocuments } from '@/lib/proposals/proposalReferenceDocuments';
import { dispatchOpenProposalWorkspace, openProposalClientView } from '@/lib/proposals/workItemWorkspace';
import AIProposalAssistant from '@/app/client-zone/modules/total-operations/components/proposals/AIProposalAssistant';
import ProposalSendDialog from '@/app/client-zone/modules/total-operations/components/proposals/ProposalSendDialog';
import { DocumentModal } from '@/components/finance/modals/DocumentModal';
import { getDocumentConfig } from '@/components/finance/modals/documentConfigs';
import { useDocumentsWorkItems, type Document, type DocumentType } from '@/hooks/useDocumentsWorkItems';
import type {
  Proposal,
  ProposalLineItem,
  ProposalStatus,
} from '@/lib/proposals/proposalTypes';
import {
  getProposalStatusLabel,
  getProposalStatusColor,
} from '@/lib/proposals/proposalConfig';
import {
  OPERATIONS_RBAC_REFRESH_REQUESTED_EVENT,
  isOperationsRbacRefreshRequestedForUser,
  type OperationsRbacRefreshDetail,
} from '@/lib/operations/rbacRefresh';
import {
  getOperationsMyFeaturesFetchInit,
  getOperationsMyFeaturesPath,
} from '@/lib/operations/myFeaturesClient';

/** Flat permission key→value settings object (replaces teamRbacLogic UserFeatureSettings). */
type UserFeatureSettings = Record<string, unknown>;

/**
 * Check if a boolean feature setting is enabled.
 * (Inlined from teamRbacLogic.isSettingEnabled — simplified for proposal-only keys
 * which have no parentKey dependencies or special-case handling.)
 */
function isSettingEnabled(settings: Record<string, unknown>, key: string): boolean {
  return !!settings[key];
}

interface ProposalTabProps {
  workItemId: string;
  workItemNumber?: string | null;
  contactId?: number | null;
  contactName?: string | null;
  isDark?: boolean;
}

interface FinanceDocumentModalState {
  isOpen: boolean;
  docType: DocumentType;
  document: Document | null;
}

function hasResolvedFeatureSetting(settings: UserFeatureSettings, key: string) {
  return Object.prototype.hasOwnProperty.call(settings, key)
    && settings[key] !== null
    && settings[key] !== undefined;
}

function resolveProposalFeaturePermission(
  settings: UserFeatureSettings,
  key: string,
  fallback: boolean,
) {
  return hasResolvedFeatureSetting(settings, key)
    ? isSettingEnabled(settings, key)
    : fallback;
}

function buildSiteBasedProposalKickoffPrompt(params: {
  workItemNumber?: string | null;
  contactName?: string | null;
}) {
  return [
    'Please assess the linked work item and generate a proposal draft using all available proposal context on the server.',
    params.workItemNumber ? `Focus on ${params.workItemNumber}.` : null,
    params.contactName ? `This is for ${params.contactName}.` : null,
    'Use the work item details, linked appointments, comments, photo evidence, commercial signals, quote requests, and any related job-thread discussion that is available.',
    'If a critical pricing or scope detail is missing, ask one focused follow-up question instead of guessing.',
  ].filter(Boolean).join(' ');
}

function formatCurrency(value: number | null | undefined, currency = 'ZAR') {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatDateTime(value?: string | null) {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }

  return parsed.toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function compactCount(value: number | null | undefined) {
  return new Intl.NumberFormat('en-ZA', {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function getProposalLineTotal(item: ProposalLineItem) {
  if (typeof item.line_total === 'number' && Number.isFinite(item.line_total)) {
    return item.line_total;
  }

  return (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
}

function getProposalFinancials(proposal: Proposal) {
  const computedSubtotal = proposal.line_items.reduce((sum, item) => sum + getProposalLineTotal(item), 0);
  const subtotal = typeof proposal.subtotal === 'number' && Number.isFinite(proposal.subtotal)
    ? proposal.subtotal
    : computedSubtotal;
  const discountTotal = typeof proposal.discount_total === 'number' && Number.isFinite(proposal.discount_total)
    ? proposal.discount_total
    : 0;
  const taxTotal = typeof proposal.tax_total === 'number' && Number.isFinite(proposal.tax_total)
    ? proposal.tax_total
    : 0;
  const grandTotal = typeof proposal.grand_total === 'number' && Number.isFinite(proposal.grand_total)
    ? proposal.grand_total
    : Math.max(0, subtotal - discountTotal) + taxTotal;

  return {
    subtotal,
    discountTotal,
    taxTotal,
    grandTotal,
  };
}

export default function ProposalTab({
  workItemId,
  workItemNumber,
  contactId,
  contactName,
  isDark = false,
}: ProposalTabProps) {
  const getErrorMessage = (error: unknown, fallback: string) => error instanceof Error ? error.message : fallback;
  const { loadDocument: loadFinanceDocument } = useDocumentsWorkItems();
  const {
    enabledConversionTargets,
    enabledDeliveryChannels,
    hasAnyDeliveryChannelEnabled,
    isSourceTypeEnabled,
  } = useAiProposalManagerSettings();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAssistant, setShowAssistant] = useState(false);
  const [assistantSessionKey, setAssistantSessionKey] = useState(0);
  const [activeProposal, setActiveProposal] = useState<Proposal | null>(null);
  const [sendDialogProposal, setSendDialogProposal] = useState<Proposal | null>(null);
  const [financeDocumentModal, setFinanceDocumentModal] = useState<FinanceDocumentModalState>({
    isOpen: false,
    docType: 'quote',
    document: null,
  });
  const [userFeatureSettings, setUserFeatureSettings] = useState<UserFeatureSettings>({});
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [permissionsUnavailable, setPermissionsUnavailable] = useState(false);

  const buildResponseErrorMessage = useCallback((response: Response, fallback: string, detail?: string | null) => {
    if (detail && detail.trim().length > 0) {
      return detail;
    }

    return `${fallback} (${response.status}${response.statusText ? ` ${response.statusText}` : ''})`;
  }, []);

  const canViewProposalTab = useMemo(() => {
    if (permissionsUnavailable) {
      return true;
    }

    return resolveProposalFeaturePermission(userFeatureSettings, 'ai_proposal_view_tab', false);
  }, [permissionsUnavailable, userFeatureSettings]);

  const canGenerateProposals = useMemo(() => {
    if (permissionsUnavailable) {
      return false;
    }

    return resolveProposalFeaturePermission(userFeatureSettings, 'ai_proposal_generate', false);
  }, [permissionsUnavailable, userFeatureSettings]);

  const canCreateSiteBasedProposals = useMemo(() => {
    return canGenerateProposals && isSourceTypeEnabled('site_based');
  }, [canGenerateProposals, isSourceTypeEnabled]);

  const canApproveProposals = useMemo(() => {
    if (permissionsUnavailable) {
      return false;
    }

    return resolveProposalFeaturePermission(userFeatureSettings, 'ai_proposal_approve', false);
  }, [permissionsUnavailable, userFeatureSettings]);

  const sourceReferences = workItemId
    ? [{
      work_item_id: workItemId,
      work_item_number: workItemNumber || null,
      work_item_type: 'work_item',
      title: workItemNumber || 'Linked Work Item',
      contact_name: contactName || null,
    }]
    : [];

  const referenceDocumentCountByProposalId = useMemo(() => {
    return Object.fromEntries(
      proposals.map((proposal) => [proposal.id, extractProposalReferenceDocuments(proposal.conversation).length]),
    ) as Record<string, number>;
  }, [proposals]);

  const activeReferenceDocuments = useMemo(() => {
    return activeProposal ? extractProposalReferenceDocuments(activeProposal.conversation) : [];
  }, [activeProposal]);

  const activeProposalFinancials = useMemo(() => {
    return activeProposal ? getProposalFinancials(activeProposal) : null;
  }, [activeProposal]);

  const activeThreadContext = useMemo(() => {
    return activeProposal?.thread_context_summary || null;
  }, [activeProposal]);

  const showWorkspace = showAssistant || Boolean(activeProposal);

  const upsertProposal = useCallback((proposal: Proposal) => {
    setProposals((prev) => {
      const existingIndex = prev.findIndex((entry) => entry.id === proposal.id);
      if (existingIndex === -1) {
        return [proposal, ...prev];
      }

      const next = [...prev];
      next[existingIndex] = proposal;
      return next;
    });

    setActiveProposal(proposal);
  }, []);

  const buildFallbackFinanceDocument = useCallback((docType: DocumentType, payload: Partial<Document> & { id: string }): Document => ({
    id: payload.id,
    doc_type: docType,
    item_type: docType,
    doc_number: payload.doc_number || payload.item_number || '',
    title: payload.title,
    status: payload.status || 'draft',
    issued_at: payload.issued_at || new Date().toISOString(),
    currency: payload.currency || 'ZAR',
    subtotal: payload.subtotal || 0,
    tax_total: payload.tax_total || 0,
    grand_total: payload.grand_total || 0,
    contact_id: payload.contact_id,
    contact_name: payload.contact_name,
    contact_email: payload.contact_email,
    contact_phone: payload.contact_phone,
    notes: payload.notes,
    internal_notes: payload.internal_notes,
    items: payload.items || [],
  }), []);

  const openFinanceDocument = useCallback(async (docId: string, docType: DocumentType, fallback?: Partial<Document>) => {
    const loadedDocument = await loadFinanceDocument(docId);
    setFinanceDocumentModal({
      isOpen: true,
      docType,
      document: loadedDocument || buildFallbackFinanceDocument(docType, { id: docId, ...fallback }),
    });
    setShowAssistant(false);
  }, [buildFallbackFinanceDocument, loadFinanceDocument]);

  const loadProposals = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetchWithRetry(
        getApiUrl(`/api/v1/proposals?parent_work_item_id=${workItemId}&limit=20`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
        {
          retries: 2,
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(buildResponseErrorMessage(response, 'Failed to load proposals', data?.error));
      }

      setProposals(data.data || []);
    } catch (error: unknown) {
      console.error('[ProposalTab] Error loading proposals:', error);
      setError(getErrorMessage(error, 'Failed to load proposals'));
    } finally {
      setLoading(false);
    }
  }, [buildResponseErrorMessage, workItemId]);

  const loadUserFeatureSettings = useCallback(async () => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        return;
      }

      const response = await fetchWithRetry(
        getApiUrl(getOperationsMyFeaturesPath()),
        getOperationsMyFeaturesFetchInit(token),
        {
          retries: 2,
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(buildResponseErrorMessage(response, 'Failed to load user feature settings', data?.error));
      }

      const data = await response.json();
      setPermissionsUnavailable(false);
      setUserFeatureSettings(data.data || data.feature_settings || data || {});
    } catch (error) {
      setPermissionsUnavailable(true);
      console.warn('[ProposalTab] User feature settings unavailable, using view-only fallback:', error);
    } finally {
      setPermissionsLoaded(true);
    }
  }, [buildResponseErrorMessage]);

  useEffect(() => {
    void loadUserFeatureSettings();
  }, [loadUserFeatureSettings]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleOperationsRbacRefresh = (event: Event) => {
      const detail = (event as CustomEvent<OperationsRbacRefreshDetail | undefined>).detail;
      const currentUserId = Number(authService.getUser()?.id);

      if (!isOperationsRbacRefreshRequestedForUser(detail, currentUserId)) {
        return;
      }

      void loadUserFeatureSettings();
    };

    const handleClientContextChanged = () => {
      void loadUserFeatureSettings();
    };

    window.addEventListener(OPERATIONS_RBAC_REFRESH_REQUESTED_EVENT, handleOperationsRbacRefresh as EventListener);
    window.addEventListener('client-switched', handleClientContextChanged as EventListener);
    window.addEventListener('activeClientChanged', handleClientContextChanged as EventListener);

    return () => {
      window.removeEventListener(OPERATIONS_RBAC_REFRESH_REQUESTED_EVENT, handleOperationsRbacRefresh as EventListener);
      window.removeEventListener('client-switched', handleClientContextChanged as EventListener);
      window.removeEventListener('activeClientChanged', handleClientContextChanged as EventListener);
    };
  }, [loadUserFeatureSettings]);

  useEffect(() => {
    void loadProposals();
  }, [loadProposals]);

  useEffect(() => {
    setActiveProposal((current) => {
      if (proposals.length === 0) {
        return showAssistant ? current : null;
      }

      if (!current) {
        return showAssistant ? current : proposals[0];
      }

      return proposals.find((proposal) => proposal.id === current.id) || proposals[0];
    });
  }, [proposals, showAssistant]);

  const handleCreateNewProposal = useCallback(async () => {
    if (!canGenerateProposals) {
      notify.error('You do not have permission to generate AI quotes');
      return;
    }

    if (!isSourceTypeEnabled('site_based')) {
      notify.error('Site-based AI quotes are disabled for this client');
      return;
    }

    setActiveProposal(null);
    setShowAssistant(true);
    setAssistantSessionKey((current) => current + 1);
  }, [canGenerateProposals, isSourceTypeEnabled]);

  const handleViewProposal = useCallback((proposal: Proposal) => {
    setActiveProposal(proposal);
    setShowAssistant(true);
  }, []);

  const handleOpenProposalWorkItem = useCallback((proposal: Proposal, initialTab: 'overview' | 'proposal' = 'overview') => {
    dispatchOpenProposalWorkspace({
      proposalId: proposal.id,
      initialTab,
    });
  }, []);

  const handleOpenConvertedDocument = useCallback((proposal: Proposal) => {
    if (!proposal.converted_document_id || !proposal.converted_document_type) {
      notify.error('This proposal has not been converted to a finance document yet');
      return;
    }

    void openFinanceDocument(proposal.converted_document_id, proposal.converted_document_type);
  }, [openFinanceDocument]);

  const handleConvertToDocument = useCallback(async (proposal: Proposal, targetType: 'quote' | 'invoice') => {
    // Legacy proposals require approved status before conversion
    // New quotes (created via the updated API) can be opened at any status
    const isLegacyProposal = proposal.proposal_number?.startsWith('PROP-');
    if (isLegacyProposal && proposal.status !== 'approved') {
      const statusLabel = getProposalStatusLabel(proposal.status);
      notify.error(`Cannot convert proposal in "${statusLabel}" status. Proposal must be approved before conversion.`);
      return;
    }

    // Check if already converted (only for legacy proposals)
    if (isLegacyProposal && proposal.converted_document_id && proposal.converted_document_type) {
      const alreadyConvertedType = proposal.converted_document_type === 'quote' ? 'Quote' : 'Invoice';
      notify.error(`Proposal has already been converted to a ${alreadyConvertedType}. Open the existing document instead.`);
      return;
    }

    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(getApiUrl('/api/v1/proposals'), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'convert_to_document',
          proposal_id: proposal.id,
          target_type: targetType,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || 'Failed to convert proposal');
      }

      const data = await response.json();
      if (data.data?.proposal) {
        upsertProposal(data.data.proposal as Proposal);
      }

      if (data.data?.document?.id && data.data?.document?.doc_type) {
        await openFinanceDocument(
          String(data.data.document.id),
          data.data.document.doc_type as DocumentType,
          data.data.document as Partial<Document>,
        );
        notify.success(`Quote opened for review.`);
      } else {
        notify.success(`Quote opened`);
      }

      void loadProposals();
    } catch (error: unknown) {
      console.error('[ProposalTab] Error opening quote:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to open quote';
      notify.error(errorMessage);
    }
  }, [loadProposals, openFinanceDocument, upsertProposal]);

  const handleApproveProposal = useCallback(async (proposal: Proposal) => {
    if (!canApproveProposals) {
      notify.error('You do not have permission to approve proposals');
      return;
    }

    // Validate state transition safety: can only approve from pending_approval, review, or draft
    const validTransitions = ['draft', 'review', 'pending_approval'];
    if (!validTransitions.includes(proposal.status)) {
      const statusLabel = getProposalStatusLabel(proposal.status);
      notify.error(`Cannot approve proposal in "${statusLabel}" status. Proposal must be in draft, review, or pending approval status.`);
      return;
    }

    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(getApiUrl(`/api/v1/proposals/${proposal.id}`), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'approved',
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to approve proposal');
      }

      if (data.data) {
        upsertProposal(data.data as Proposal);
      }

      notify.success('Proposal approved and ready for conversion to quote or invoice');
      void loadProposals();
    } catch (error: unknown) {
      console.error('[ProposalTab] Error approving proposal:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to approve proposal';
      notify.error(errorMessage);
    }
  }, [canApproveProposals, loadProposals, upsertProposal]);

  const handleRejectProposal = useCallback(async (proposal: Proposal) => {
    if (!canApproveProposals) {
      notify.error('You do not have permission to reject proposals');
      return;
    }

    // Validate state transition safety: can only reject from pending_approval, review, or draft
    const validTransitions = ['draft', 'review', 'pending_approval'];
    if (!validTransitions.includes(proposal.status)) {
      const statusLabel = getProposalStatusLabel(proposal.status);
      notify.error(`Cannot reject proposal in "${statusLabel}" status. Proposal must be in draft, review, or pending approval status.`);
      return;
    }

    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(getApiUrl(`/api/v1/proposals/${proposal.id}`), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'rejected',
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to reject proposal');
      }

      if (data.data) {
        upsertProposal(data.data as Proposal);
      }

      notify.success('Proposal rejected. You can edit and re-submit if needed.');
      void loadProposals();
    } catch (error: unknown) {
      console.error('[ProposalTab] Error rejecting proposal:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to reject proposal';
      notify.error(errorMessage);
    }
  }, [canApproveProposals, loadProposals, upsertProposal]);

  const getStatusIcon = (status: ProposalStatus) => {
    switch (status) {
      case 'draft':
        return <FileText className="w-4 h-4" />;
      case 'review':
        return <Clock className="w-4 h-4" />;
      case 'pending_approval':
        return <Clock className="w-4 h-4" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      case 'converted':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  if (!permissionsLoaded || loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!canViewProposalTab) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <ShieldCheck className="w-12 h-12 text-gray-400 mb-3" />
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          Access Restricted
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          You do not have permission to view the AI Proposal tab. Contact your administrator to request access.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <AlertCircle className="w-12 h-12 text-gray-400 mb-3" />
        <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
        <button
          onClick={() => void loadProposals()}
          className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {permissionsUnavailable && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${isDark
          ? 'border-amber-800/60 bg-amber-950/30 text-amber-200'
          : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}>
          Quote permissions could not be refreshed right now. View-only mode is active until the service responds again.
        </div>
      )}

      {financeDocumentModal.document && (
        <DocumentModal
          isOpen={financeDocumentModal.isOpen}
          onClose={() => setFinanceDocumentModal({ isOpen: false, docType: 'quote', document: null })}
          doc_type={financeDocumentModal.docType}
          config={getDocumentConfig(financeDocumentModal.docType)}
          document={financeDocumentModal.document}
          // Work-item proposal surface — no finance permission context here,
          // so keep the prior UI-permissive set (backend enforces RBAC).
          permissions={{ can_view: true, can_create: true, can_edit: true, can_delete: true, can_admin: false }}
          onSaved={(document) => {
            setFinanceDocumentModal((prev) => ({ ...prev, document }));
          }}
        />
      )}

      {sendDialogProposal && (
        <ProposalSendDialog
          isOpen={Boolean(sendDialogProposal)}
          proposal={sendDialogProposal}
          allowedSendMethods={enabledDeliveryChannels}
          initialSendMethod="email"
          onClose={() => setSendDialogProposal(null)}
          onSent={(proposal) => {
            upsertProposal(proposal);
            void loadProposals();
          }}
        />
      )}

      <div className={`overflow-hidden rounded-2xl border ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-white'}`}>
        <div className={`border-b px-5 py-4 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-slate-50/80'}`}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Quote workspace
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Keep the AI assistant as a focused sidecar while the actual quote renders here in a structured, finance-style workspace.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => void loadProposals()}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                <RefreshCw className="w-4 h-4" />
                Reload
              </button>
              {canCreateSiteBasedProposals && (
                <button
                  onClick={() => void handleCreateNewProposal()}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:bg-blue-400"
                >
                  <Bot className="w-4 h-4" />
                  Generate AI Quote
                </button>
              )}
            </div>
          </div>
        </div>

        {showWorkspace ? (
          <div className="grid gap-4 p-4 xl:grid-cols-[minmax(0,1.45fr)_360px] xl:items-start">
            <div className="space-y-4">
              {activeProposal ? (
                <div className={`overflow-hidden rounded-xl border ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-white'}`}>
                  <div className={`border-b px-4 py-4 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-slate-50/70'}`}>
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {activeProposal.title}
                          </h4>
                          {activeProposal.proposal_number && (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              #{activeProposal.proposal_number}
                            </span>
                          )}
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-medium">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${getProposalStatusColor(activeProposal.status)}`}>
                            {getStatusIcon(activeProposal.status)}
                            {getProposalStatusLabel(activeProposal.status)}
                          </span>
                          <span className={`rounded-full px-2.5 py-1 ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600 shadow-sm'}`}>
                            {activeProposal.source_type === 'site_based' ? 'Site-based quote' : 'Standalone quote'}
                          </span>
                          <span className={`rounded-full px-2.5 py-1 ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600 shadow-sm'}`}>
                            Updated {formatDateTime(activeProposal.updated_at)}
                          </span>
                        </div>

                        <p className="mt-3 max-w-4xl text-sm text-gray-600 dark:text-gray-400">
                          {activeProposal.description?.trim()
                            || 'No quote summary has been captured yet. Use the assistant to shape the commercial story, scope, and pricing.'}
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                        <button
                          onClick={() => handleOpenProposalWorkItem(activeProposal, 'overview')}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 dark:border-blue-900/40"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Open full workspace
                        </button>
                        {activeProposal.proposal_permalink_url && (
                          <button
                            onClick={() => openProposalClientView(activeProposal.proposal_permalink_url!)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Open client view
                          </button>
                        )}
                        {(activeProposal.status === 'draft' || activeProposal.status === 'review' || activeProposal.status === 'rejected') && hasAnyDeliveryChannelEnabled && (
                          <button
                            onClick={() => setSendDialogProposal(activeProposal)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-950/50"
                          >
                            <Send className="w-3.5 h-3.5" />
                            {activeProposal.status === 'rejected' ? 'Re-send to Client' : 'Send to Client'}
                          </button>
                        )}
                        {activeProposal.status === 'pending_approval' && canApproveProposals && (
                          <>
                            <button
                              onClick={() => void handleApproveProposal(activeProposal)}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700 dark:border-emerald-900/40"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                              Approve
                            </button>
                            <button
                              onClick={() => void handleRejectProposal(activeProposal)}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-100 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/40"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                              Reject
                            </button>
                          </>
                        )}
                        {activeProposal.status === 'approved' && enabledConversionTargets.includes('quote') && (
                          <>
                            <button
                              onClick={() => void handleConvertToDocument(activeProposal, 'quote')}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-600 px-3 py-2 text-xs font-medium text-white hover:bg-purple-700 dark:border-purple-900/40"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              Open Quote
                            </button>
                            {enabledConversionTargets.includes('invoice') && (
                              <button
                                onClick={() => void handleConvertToDocument(activeProposal, 'invoice')}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 px-3 py-2 text-xs font-medium text-purple-700 hover:bg-purple-100 dark:border-purple-900/40 dark:text-purple-300 dark:hover:bg-purple-950/40"
                              >
                                <DollarSign className="w-3.5 h-3.5" />
                                Convert to Invoice
                              </button>
                            )}
                          </>
                        )}
                        {activeProposal.status === 'approved' && !enabledConversionTargets.includes('quote') && enabledConversionTargets.includes('invoice') && (
                          <button
                            onClick={() => void handleConvertToDocument(activeProposal, 'invoice')}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-600 px-3 py-2 text-xs font-medium text-white hover:bg-purple-700 dark:border-purple-900/40"
                          >
                            <DollarSign className="w-3.5 h-3.5" />
                            Convert to Invoice
                          </button>
                        )}
                        {activeProposal.status === 'converted' && activeProposal.converted_document_id && activeProposal.converted_document_type && (
                          <button
                            onClick={() => handleOpenConvertedDocument(activeProposal)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700 dark:border-indigo-900/40"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Open {activeProposal.converted_document_type === 'invoice' ? 'Invoice' : 'Quote'}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      {[
                        {
                          label: 'Client',
                          value: activeProposal.contact_name || contactName || 'Unassigned',
                        },
                        {
                          label: 'Line items',
                          value: compactCount(activeProposal.line_items.length),
                        },
                        {
                          label: 'Reference docs',
                          value: compactCount(activeReferenceDocuments.length),
                        },
                        {
                          label: 'Quote total',
                          value: formatCurrency(activeProposalFinancials?.grandTotal, activeProposal.currency),
                          tone: 'text-emerald-700 dark:text-emerald-300',
                        },
                      ].map((metric) => (
                        <div key={metric.label} className={`rounded-xl border px-4 py-3 ${isDark ? 'border-gray-700 bg-gray-800/70' : 'border-gray-200 bg-white/80'}`}>
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{metric.label}</p>
                          <p className={`mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100 ${metric.tone || ''}`}>{metric.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <div className={`grid min-w-[720px] grid-cols-[minmax(0,1.8fr)_0.55fr_0.8fr_0.8fr] gap-3 px-4 py-3 text-xs font-semibold uppercase tracking-wide ${isDark ? 'bg-gray-900/70 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                      <span>Description</span>
                      <span>Qty</span>
                      <span>Unit Price</span>
                      <span className="text-right">Line Total</span>
                    </div>

                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {activeProposal.line_items.length === 0 ? (
                        <div className="px-4 py-10 text-center">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">No line items yet</p>
                          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                            Ask the assistant to generate scope, pricing, or exclusions and they will render here instead of hiding inside a chat bubble. Much healthier for morale.
                          </p>
                        </div>
                      ) : (
                        activeProposal.line_items.map((item, index) => (
                          <div
                            key={item.id || `${item.description}-${index}`}
                            className="grid min-w-[720px] grid-cols-[minmax(0,1.8fr)_0.55fr_0.8fr_0.8fr] gap-3 px-4 py-3 text-sm"
                          >
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                  {item.description}
                                </p>
                                {(item.category || item.item_code) && (
                                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                    {item.category || item.item_code}
                                  </span>
                                )}
                              </div>
                              {item.notes && (
                                <p className="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                                  {item.notes}
                                </p>
                              )}
                            </div>
                            <span className="text-gray-600 dark:text-gray-300">{compactCount(item.quantity)}</span>
                            <span className="text-gray-600 dark:text-gray-300">{formatCurrency(item.unit_price, activeProposal.currency)}</span>
                            <span className="text-right font-medium text-gray-900 dark:text-gray-100">{formatCurrency(getProposalLineTotal(item), activeProposal.currency)}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="grid gap-4 p-4 xl:grid-cols-[minmax(0,1fr)_320px]">
                    <div className="space-y-4">
                      <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50/80'}`}>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Quote summary</p>
                        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {activeProposal.description?.trim() || 'No quote summary has been captured yet.'}
                        </p>
                      </div>

                      <div className="grid gap-4 lg:grid-cols-2">
                        <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50/80'}`}>
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Client-facing notes</p>
                          <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                            {activeProposal.notes?.trim() || 'No client-facing notes have been captured yet.'}
                          </p>
                        </div>

                        <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50/80'}`}>
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Internal notes</p>
                          <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                            {activeProposal.internal_notes?.trim() || 'No internal notes have been captured yet.'}
                          </p>
                        </div>
                      </div>

                      <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50/80'}`}>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Source context</p>
                            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                              {activeProposal.source_references.length > 0
                                ? activeProposal.source_references.map((reference) => reference.work_item_number || reference.title).join(', ')
                                : workItemNumber || 'No linked source work items stored yet.'}
                            </p>
                          </div>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600 shadow-sm'}`}>
                            {compactCount(activeProposal.source_references.length)} linked reference{activeProposal.source_references.length === 1 ? '' : 's'}
                          </span>
                        </div>

                        {activeReferenceDocuments.length > 0 && (
                          <div className="mt-4 grid gap-2">
                            {activeReferenceDocuments.slice(0, 3).map((document) => (
                              <div
                                key={`${document.id}-${document.title}`}
                                className={`rounded-lg border px-3 py-3 ${isDark ? 'border-gray-700 bg-gray-800/80' : 'border-gray-200 bg-white'}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg ${isDark ? 'bg-gray-900 text-gray-200' : 'bg-slate-100 text-slate-600'}`}>
                                    <FileStack className="h-4 w-4" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{document.title}</p>
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                      {document.fileName || document.attachmentType}
                                      {document.extractedFrom ? ` · ${document.extractedFrom.toUpperCase()}` : ''}
                                    </p>
                                    {document.textPreview && (
                                      <p className="mt-2 line-clamp-2 text-xs text-gray-600 dark:text-gray-300">{document.textPreview}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {activeThreadContext && activeThreadContext.messages.length > 0 && (
                          <div className="mt-4 rounded-xl border border-dashed px-4 py-4 dark:border-gray-700">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">AI proposal thread extract</p>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                                  {activeThreadContext.team_name || activeThreadContext.thread_name || 'AI Proposal Manager thread'}
                                  {activeThreadContext.work_item_number ? ` · linked to ${activeThreadContext.work_item_number}` : ''}
                                </p>
                              </div>
                              {activeThreadContext.proposal_url && (
                                <button
                                  onClick={() => openProposalClientView(activeThreadContext.proposal_url || undefined)}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  Open live proposal
                                </button>
                              )}
                            </div>

                            {activeThreadContext.summary && (
                              <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                {activeThreadContext.summary}
                              </p>
                            )}

                            <div className="mt-3 space-y-2">
                              {activeThreadContext.messages.slice(-4).map((message, index) => (
                                <div
                                  key={`${activeThreadContext.thread_id}-${message.created_at}-${index}`}
                                  className={`rounded-lg border px-3 py-3 ${isDark ? 'border-gray-700 bg-gray-800/80' : 'border-gray-200 bg-white'}`}
                                >
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                      {message.sender_name || 'Thread message'}
                                    </span>
                                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                      {formatDateTime(message.created_at)}
                                    </span>
                                  </div>
                                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                    {message.content}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-white'}`}>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Commercial summary</p>
                      <div className="mt-4 space-y-3 text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-gray-600 dark:text-gray-400">Included lines</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{compactCount(activeProposal.line_items.length)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(activeProposalFinancials?.subtotal, activeProposal.currency)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-gray-600 dark:text-gray-400">Discount</span>
                          <span className="font-medium text-amber-700 dark:text-amber-300">{formatCurrency(activeProposalFinancials?.discountTotal, activeProposal.currency)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-gray-600 dark:text-gray-400">Tax</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(activeProposalFinancials?.taxTotal, activeProposal.currency)}</span>
                        </div>
                        <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-base font-semibold text-gray-900 dark:text-gray-100">Grand total</span>
                            <span className="text-lg font-semibold text-emerald-700 dark:text-emerald-300">{formatCurrency(activeProposalFinancials?.grandTotal, activeProposal.currency)}</span>
                          </div>
                        </div>
                      </div>

                      <div className={`mt-4 rounded-lg border px-3 py-3 text-xs ${isDark ? 'border-gray-700 bg-gray-800/80 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                        This mirrors the proposal as structured commercial data, so the team can inspect scope and totals at a glance instead of reverse-engineering intent from chat history.
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`flex h-full min-h-[520px] flex-col justify-center rounded-xl border border-dashed p-8 text-center ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-300 bg-gray-50/70'}`}>
                  <Bot className="mx-auto mb-4 h-10 w-10 text-blue-500" />
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    New proposal draft
                  </h4>
                  <p className="mx-auto mt-3 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
                    The assistant is ready on the right. As soon as it creates or revises the proposal, the structured preview will render here with line items, notes, and totals.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-4 xl:sticky xl:top-4">
              <div className={`overflow-hidden rounded-xl border ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-white'}`}>
                <div className={`border-b px-4 py-3 ${isDark ? 'border-gray-700 bg-gray-900/70' : 'border-gray-200 bg-slate-50/80'}`}>
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-blue-500" />
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      AI proposal copilot
                    </h4>
                  </div>
                  <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    Keep the conversation focused here while the commercial structure stays visible beside it.
                  </p>
                </div>

                <div className="h-[620px] overflow-hidden">
                  <AIProposalAssistant
                    key={`proposal-tab-assistant-${assistantSessionKey}`}
                    proposal={activeProposal}
                    proposalId={activeProposal?.id}
                    sourceType={workItemId ? 'site_based' : 'standalone'}
                    sourceReferences={sourceReferences}
                    contextWorkItemId={workItemId}
                    contactId={contactId}
                    compact
                    embedded
                    includeContextMessage={false}
                    onProposalCreated={(proposal) => {
                      upsertProposal(proposal);
                      void loadProposals();
                    }}
                    onProposalUpdated={(proposal) => {
                      upsertProposal(proposal);
                    }}
                    onOpenConvertedDocument={handleOpenConvertedDocument}
                    onSendToClient={(proposal) => {
                      upsertProposal(proposal);
                      void loadProposals();
                    }}
                    onConvertToDocument={(proposal, targetType) => {
                      void handleConvertToDocument(proposal, targetType);
                    }}
                    initialPrompt={!activeProposal && showAssistant && workItemId
                      ? buildSiteBasedProposalKickoffPrompt({
                        workItemNumber,
                        contactName,
                      })
                      : undefined}
                    showProviderToggle={false}
                  />
                </div>
              </div>

              {activeProposal && (
                <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-white'}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Workspace snapshot</p>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    {[
                      {
                        label: 'Status',
                        value: getProposalStatusLabel(activeProposal.status),
                      },
                      {
                        label: 'Last updated',
                        value: formatDateTime(activeProposal.updated_at),
                      },
                      {
                        label: 'Revisions',
                        value: compactCount(activeProposal.revisions?.length),
                      },
                      {
                        label: 'Valid until',
                        value: formatDateTime(activeProposal.valid_until),
                      },
                    ].map((item) => (
                      <div key={item.label} className={`rounded-lg border px-3 py-3 ${isDark ? 'border-gray-700 bg-gray-800/80' : 'border-gray-200 bg-gray-50/80'}`}>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{item.label}</p>
                        <p className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-8 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                No quotes yet
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {canCreateSiteBasedProposals
                  ? 'Generate an AI-powered quote using the linked work item context, including comments, photos, commercial signals, quote requests, and related job-thread discussion when available.'
                  : canGenerateProposals
                    ? 'Site-based AI quotes are disabled for this client in Industry Solution Settings.'
                    : 'You do not have permission to generate AI quotes'
                }
              </p>
              {canCreateSiteBasedProposals && (
                <button
                  onClick={() => void handleCreateNewProposal()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Bot className="w-4 h-4" />
                  Generate AI Quote
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {proposals.length > 0 && (
        <div className={`rounded-2xl border ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-white'}`}>
          <div className={`border-b px-5 py-4 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-slate-50/80'}`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Linked quotes
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {proposals.length} quote{proposals.length !== 1 ? 's' : ''} linked to this work item.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3 p-4">
            {proposals.map((proposal) => {
              const isSelected = activeProposal?.id === proposal.id;

              return (
                <div
                  key={proposal.id}
                  className={`rounded-xl border p-4 transition-colors ${isSelected
                    ? isDark
                      ? 'border-blue-600 bg-blue-950/20'
                      : 'border-blue-300 bg-blue-50/70'
                    : isDark
                      ? 'border-gray-700 bg-gray-800/50 hover:bg-gray-800'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">
                          {proposal.title}
                        </h4>
                        {proposal.proposal_number && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            #{proposal.proposal_number}
                          </span>
                        )}
                        {isSelected && (
                          <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-medium text-white">
                            Focused in workspace
                          </span>
                        )}
                        {proposal.thread_context_summary?.thread_id && (
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${isDark ? 'bg-violet-900/40 text-violet-300' : 'bg-violet-100 text-violet-700'}`}>
                            Thread-backed draft
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${getProposalStatusColor(proposal.status)}`}>
                          {getStatusIcon(proposal.status)}
                          {getProposalStatusLabel(proposal.status)}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {formatCurrency(proposal.grand_total, proposal.currency)}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {proposal.line_items.length} items
                        </span>
                        {(referenceDocumentCountByProposalId[proposal.id] || 0) > 0 && (
                          <span className="flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            {referenceDocumentCountByProposalId[proposal.id]} ref doc{referenceDocumentCountByProposalId[proposal.id] === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>

                      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {proposal.description?.trim() || 'No summary captured yet.'}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                      <button
                        onClick={() => handleViewProposal(proposal)}
                        className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${isSelected
                          ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'
                          : 'border-blue-200 text-blue-700 hover:bg-blue-100 dark:border-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-950/40'
                          }`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        {isSelected ? 'Viewing here' : 'Show in workspace'}
                      </button>

                      <button
                        onClick={() => handleOpenProposalWorkItem(proposal, 'overview')}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 dark:border-blue-900/40"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Open full workspace
                      </button>

                      {proposal.proposal_permalink_url && (
                        <button
                          onClick={() => openProposalClientView(proposal.proposal_permalink_url!)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Client view
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
